import React, { useState } from 'react';
import { RiAddFill, RiCloseLine, RiEditLine } from 'react-icons/ri';
import {
  Button,
  Divider,
  HelperText,
  IconButton,
  Modal,
  TextInput,
} from '../../../../../components/UiComponents/Ui';

import {
  editBusinessIDsSchema,
} from './setupValidators';
import { submitUpdateSupplierBusinessIDs } from './setupActions';
import { useGlobalContext } from '../../../../../Contexts/GlobalContext';
import { getError, handleChange } from '../../../../helperFunctions';
import SubmitButton from '../../../../../components/UiComponents/SubmitButton';



const EditBusinessIDsModal = ({
  supplierDetails,
  supplierDetailsMutate,
}) => {
  const { setSnackbar } = useGlobalContext();
  // null = closed, 1 = view, 2 = edit
  const [editBusinessIDs, setEditBusinessIDs] = useState(null);
  const [businessIDsData, setBusinessIDsData] =
    useState({});
  const [formErrors, setFormErrors] = useState({});

  const handleClose = () => {
    setEditBusinessIDs(null);
    setBusinessIDsData({});
    setFormErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const validationResult = editBusinessIDsSchema.safeParse({
        companyCodes: businessIDsData?.companyCodes || [],
        _id: supplierDetails?._id,
      });
      if (validationResult.success) {
        const response = await submitUpdateSupplierBusinessIDs(
          validationResult.data,
        );
        if (!response?.error) {
          setSnackbar({
            open: true,
            message: 'Supplier Information updated Successfully!',
            type: 'success',
          })
          supplierDetailsMutate();
          handleClose();
        } else {
          setSnackbar({
            open: true,
            message: 'Internal Server Error! Please try again!',
            type: 'error',
          })
        }
      } else {
        setFormErrors(formatZodError(validationResult.error.errors));
      }
    } catch (error) {
      console.log('error in handleSubmit', error);
      handleClose();
      setSnackbar({
        open: true,
        message: 'Internal Server Error! Please try again!',
        type: 'error',
      });
    }
  };

  const startEditing = () => {
    setEditBusinessIDs(2);
    setBusinessIDsData({
      companyCodes: supplierDetails?.companyCodes?.length
        ? [...supplierDetails.companyCodes]
        : [{ label: '', value: '' }],
    });
  };

  return (
    <div>
      <IconButton
        aria-label="Edit business IDs"
        variant="ghost"
        onClick={() => setEditBusinessIDs(1)}
      >
        <RiEditLine size={20} />
      </IconButton>

      <Modal
        showModal={editBusinessIDs !== null}
        setShowModal={handleClose}
        closeOnOutsideClick={false}
        width={438}
      >
        <form className="space-y-[18px]" onSubmit={handleSubmit}>
          <h2 className="text-lg font-semibold text-gray-800">Business IDs</h2>
          <Divider />

          {editBusinessIDs === 1 ? (
            <div className="space-y-2">
              {supplierDetails?.companyCodes?.length === 0 ? (
                <div className="flex h-[250px] flex-col items-center justify-center space-y-4">
                  <p className="text-md font-medium text-gray-400">
                    No business IDs added.
                  </p>
                  <Button
                    variant="soft"
                    startIcon={<RiAddFill size={20} />}
                    onClick={startEditing}
                  >
                    Add Business IDs
                  </Button>
                </div>
              ) : (
                <div className="containerScroll max-h-[250px] space-y-2 overflow-y-auto pr-2">
                  {supplierDetails?.companyCodes?.map((d, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between rounded-lg border px-4 py-2"
                    >
                      <p className="text-sm font-regular text-gray-500">
                        {d.label}
                      </p>
                      <p className="text-sm font-medium text-gray-800">
                        {d.value}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="relative flex flex-col space-y-3">
              <div className="containerScroll max-h-[250px] space-y-3 overflow-y-auto pr-2">
                {businessIDsData?.companyCodes?.map((entry, i) => {
                  const labelError = getError(
                    ['companyCodes', i, 'label'],
                    formErrors,
                  );
                  const valueError = getError(
                    ['companyCodes', i, 'value'],
                    formErrors,
                  );
                  return (
                    <div key={i} className="flex items-start gap-2">
                      <div className="flex-1 space-y-1">
                        <TextInput
                          placeholder="Label"
                          value={entry.label}
                          error={labelError}
                          onChange={(e) =>
                            handleChange(
                              ['companyCodes', i, 'label'],
                              e.target.value,
                              setBusinessIDsData,
                              setFormErrors,
                              formErrors,
                            )
                          }
                        />
                        {!!labelError && <HelperText>{labelError}</HelperText>}
                      </div>
                      <div className="flex-1 space-y-1">
                        <TextInput
                          placeholder="Value"
                          value={entry.value}
                          error={valueError}
                          onChange={(e) =>
                            handleChange(
                              ['companyCodes', i, 'value'],
                              e.target.value,
                              setBusinessIDsData,
                              setFormErrors,
                              formErrors,
                            )
                          }
                        />
                        {!!valueError && <HelperText>{valueError}</HelperText>}
                      </div>
                      {(businessIDsData?.companyCodes || []).length > 1 && (
                        <IconButton
                          aria-label="Remove ID"
                          variant="ghost"
                          intent="error"
                          onClick={() =>
                            setBusinessIDsData((prev) => ({
                              ...prev,
                              companyCodes: prev.companyCodes?.filter(
                                (_, idx) => idx !== i,
                              ),
                            }))
                          }
                        >
                          <RiCloseLine size={18} />
                        </IconButton>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="sticky bottom-0 bg-inherit pt-2">
                <Button
                  variant="soft"
                  startIcon={<RiAddFill size={18} />}
                  onClick={() =>
                    setBusinessIDsData((prev) => ({
                      ...prev,
                      companyCodes: [
                        ...(prev.companyCodes ?? []),
                        { label: '', value: '' },
                      ],
                    }))
                  }
                >
                  Add More
                </Button>
              </div>
            </div>
          )}

          <Divider />
          <div className="flex items-center justify-end gap-2">
            {!!supplierDetails?.companyCodes?.length &&
              editBusinessIDs !== 2 && (
                <Button
                  variant="soft"
                  startIcon={<RiAddFill size={20} />}
                  onClick={startEditing}
                >
                  Add / Update  IDs
                </Button>
              )}
            <Button variant="outlined" intent="default" onClick={handleClose}>
              Cancel
            </Button>
            {editBusinessIDs === 2 && <SubmitButton name="Save Changes" />}
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default EditBusinessIDsModal;
